// ============================================================================
// AWS S3 MULTIPART UPLOAD - SHARED UTILITIES
// ============================================================================
// Handles large file uploads (1GB+) by splitting into 20MB chunks and uploading
// concurrently to S3 with automatic retry and timeout protection.
//
// Used by:
// - usePartnerCodeGroupForm (uploadType: "PARTNER_CODE_GROUP" | "CODE_GROUP")
// - useWinningScheduleUpload (uploadType: "PRIZE_SEED")
// - useLeaderboardUpload (uploadType: "LEADERBOARD")

export const MULTIPART_PART_SIZE_BYTES = 20 * 1024 * 1024; // 20 MB per part
const MULTIPART_UPLOAD_CONCURRENCY = 10; // Upload 10 parts simultaneously
const MULTIPART_UPLOAD_MAX_ATTEMPTS = 3; // Retry failed parts up to 3 times
const MULTIPART_UPLOAD_TIMEOUT_MS = 25 * 60 * 1000; // 25-minute timeout per part
const MULTIPART_UPLOAD_RETRY_DELAY_MS = 1000; // Initial delay before retry (exponential backoff)

/**
 * Utility: Sleep for specified milliseconds
 * Used for exponential backoff between retry attempts
 */
const wait = (delay) => new Promise((resolve) => setTimeout(resolve, delay));

/**
 * Utility: Clean up malformed S3 pre-signed URLs
 * Removes trailing encoded quotes (%22) or literal quotes that break signatures
 */
const getUploadUrl = (url) => url?.replace(/(?:%22|")$/, "");

/**
 * Upload a single file part to S3 with automatic retry and timeout
 *
 * Flow:
 * 1. Calculate byte range for this part number
 * 2. Retry up to 3 times on failure with exponential backoff
 * 3. Set 25-minute timeout per attempt (abort if exceeded)
 * 4. Extract ETag from response (required for multipart completion)
 *
 * @param {Object} part - Part metadata { partNumber, url }
 * @param {File} file - File object to upload
 * @param {number} partSizeBytes - Size of each part (from backend)
 * @param {string} [uploadContentType] - Exact content type bound into the signed URL
 * @returns {Object} { partNumber, eTag } for multipart completion
 * @throws {Error} After 3 failed attempts
 */
const uploadPartWithRetry = async (part, file, partSizeBytes, uploadContentType) => {
  // Calculate byte range for this part
  const start = (part.partNumber - 1) * partSizeBytes;
  const end = Math.min(start + partSizeBytes, file.size);

  // Validate byte range
  if (start < 0 || end <= start) {
    throw new Error(`Invalid byte range for upload part ${part.partNumber}`);
  }

  let lastError;
  // Retry loop: attempt up to 3 times
  for (let attempt = 0; attempt < MULTIPART_UPLOAD_MAX_ATTEMPTS; attempt += 1) {
    let timeoutId;
    // Create abort controller for timeout handling
    const controller =
      typeof AbortController !== "undefined" ? new AbortController() : null;
    if (controller) {
      // Set 25-minute timeout; abort if exceeded
      timeoutId = setTimeout(() => controller.abort(), MULTIPART_UPLOAD_TIMEOUT_MS);
    }

    try {
      const body = uploadContentType
        ? file.slice(start, end, uploadContentType)
        : file.slice(start, end);
      // Upload part to S3 via pre-signed URL
      const uploadResponse = await fetch(getUploadUrl(part.url), {
        method: "PUT",
        ...(uploadContentType
          ? { headers: { "Content-Type": uploadContentType } }
          : {}),
        body,
        ...(controller ? { signal: controller.signal } : {}),
      });

      // Check HTTP status
      if (!uploadResponse.ok) {
        throw new Error(
          `Failed to upload part ${part.partNumber} (HTTP ${uploadResponse.status})`
        );
      }

      // Extract ETag (required for multipart completion)
      const eTag = uploadResponse.headers.get("ETag")?.replace(/"/g, "");
      if (!eTag) {
        throw new Error(`Missing ETag for upload part ${part.partNumber}`);
      }

      // Success: return part metadata
      return { partNumber: part.partNumber, eTag };
    } catch (error) {
      lastError = error;
      // On last attempt, throw error; otherwise retry with backoff
      if (attempt === MULTIPART_UPLOAD_MAX_ATTEMPTS - 1) {
        throw lastError;
      }
      // Exponential backoff: 1s, 2s, 4s
      await wait(MULTIPART_UPLOAD_RETRY_DELAY_MS * 2 ** attempt);
    } finally {
      // Always clear timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  }

  throw lastError;
};

/**
 * Upload all file parts concurrently with worker pool
 *
 * Flow:
 * 1. Create 10 concurrent workers (or fewer if fewer parts)
 * 2. Each worker pulls next part from queue and uploads with retry
 * 3. Collect all completed parts and sort by part number
 * 4. Return sorted parts for multipart completion
 *
 * @param {Array} parts - Array of part metadata { partNumber, url }
 * @param {File} file - File object to upload
 * @param {number} partSizeBytes - Size of each part (from backend)
 * @param {string} [uploadContentType] - Exact content type bound into the signed URL
 * @returns {Array} Sorted array of { partNumber, eTag } for completion
 */
const uploadParts = async (parts, file, partSizeBytes, uploadContentType) => {
  const completedParts = [];
  let nextPartIndex = 0;

  // Worker function: pull parts from queue and upload
  const uploadWorker = async () => {
    while (nextPartIndex < parts.length) {
      const part = parts[nextPartIndex];
      nextPartIndex += 1;
      // Upload part with retry; collect result
      completedParts.push(
        await uploadPartWithRetry(part, file, partSizeBytes, uploadContentType)
      );
    }
  };

  // Create worker pool (10 concurrent uploads or fewer if fewer parts)
  const workerCount = Math.min(MULTIPART_UPLOAD_CONCURRENCY, parts.length);
  await Promise.all(Array.from({ length: workerCount }, () => uploadWorker()));

  // Sort by part number (required for multipart completion)
  return completedParts.sort(
    (first, second) => first.partNumber - second.partNumber
  );
};

/**
 * Execute a multipart upload for a single file.
 *
 * Three-step process:
 * 1. Init: Call backend to get upload ID, part URLs, and part size
 * 2. Upload: Concurrently upload all parts to S3 with retry
 * 3. Complete: Tell backend upload is done (triggers S3 assembly)
 *
 * @param {Object} params
 * @param {File} params.file - File to upload
 * @param {string} params.entityId - Entity to associate upload with (e.g. codeGroupId, instantWinId)
 * @param {string} params.uploadType - Upload type (e.g. "PARTNER_CODE_GROUP", "CODE_GROUP", "PRIZE_SEED")
 * @param {Function} params.initMutation - GraphQL initMultipartUpload mutation function
 * @param {Function} params.completeMutation - GraphQL completeMultipartUpload mutation function
 * @param {string} [params.contentType] - Content type override (defaults to file.type or "application/octet-stream")
 * @param {Object} [params.initInput] - Context-specific fields for the init request
 * @returns {Object} { uploadId, eventId, completeData } on success
 * @throws {Error} On init, upload, or completion failure
 */
export async function executeMultipartUpload({
  file,
  entityId,
  uploadType,
  initMutation,
  completeMutation,
  contentType,
  initInput = {},
}) {
  // STEP 1: Init multipart upload
  // Call backend to allocate upload ID, generate pre-signed S3 URLs, and return part size
  const initResponse = await initMutation({
    variables: {
      input: {
        type: uploadType,
        fileName: file.name,
        fileSize: file.size,
        contentType: contentType || file.type || "application/octet-stream",
        entityId,
        partSizeBytes: MULTIPART_PART_SIZE_BYTES,
        ...initInput,
      },
    },
  });

  // Extract response data
  const initData = initResponse?.data?.initMultipartUpload;
  const { uploadId, fileId, objectKey, parts } = initData || {};
  const partSizeBytes = Number(initData?.partSizeBytes); // Actual part size from backend
  const uploadContentType =
    typeof initData?.contentType === "string" &&
    initData.contentType.trim().length > 0
      ? initData.contentType
      : undefined;

  // Validate init response
  if (
    !uploadId ||
    !Array.isArray(parts) ||
    parts.length === 0 ||
    !Number.isSafeInteger(partSizeBytes) ||
    partSizeBytes <= 0
  ) {
    throw new Error("Invalid multipart upload initialization response");
  }

  // STEP 2: Upload all parts concurrently to S3
  const completedParts = await uploadParts(
    parts,
    file,
    partSizeBytes,
    uploadContentType
  );

  // STEP 3: Complete multipart upload
  // Tell backend all parts are uploaded. Backend assembles parts in S3 and finalizes.
  const completeResponse = await completeMutation({
    variables: {
      input: {
        type: uploadType,
        uploadId,
        entityId,
        fileId,
        objectKey,
        parts: completedParts,
      },
    },
  });

  // Validate completion response
  const completeData = completeResponse?.data?.completeMultipartUpload;
  if (!completeData) {
    throw new Error("Multipart upload completion failed");
  }

  return { uploadId, eventId: completeData.eventId, completeData };
}
